(function registerManualPostingRules(scope) {
  const noVatAccountCodes = new Set([
    '7040',
    '7100',
    '7155',
    '7350',
    '7360',
    '7420',
    '7500',
    '7770',
  ]);

  const accountCode = (value) => {
    if (value && typeof value === 'object') {
      return accountCode(value.accountCode || value.value || value.code || value.label || '');
    }
    return String(value || '').trim().match(/^([0-9][0-9A-Za-z._-]*)/)?.[1]?.split(':')[0] || '';
  };

  const accountIdentity = (value) => {
    if (value && typeof value === 'object') {
      return accountIdentity(value.value || value.code || value.label || value.accountCode || '');
    }
    const code = String(value || '').trim().match(/^([0-9][0-9A-Za-z:._-]*)/)?.[1] || '';
    return /^(1500|2400):/.test(code) ? code.split(':')[0] : code;
  };

  const mappedAccountCode = (value, chartAccounts = []) => {
    const code = accountCode(value);
    const configured = (Array.isArray(chartAccounts) ? chartAccounts : []).find((account) =>
      accountCode(account) === code,
    );
    return accountCode(
      value?.equivalentCode ||
      value?.standardAccountCode ||
      value?.yearClosingStandardAccountCode ||
      configured?.equivalentCode ||
      configured?.standardAccountCode ||
      configured?.yearClosingStandardAccountCode ||
      code,
    );
  };

  const vatEligibility = (value, { country = 'NO', chartAccounts = [] } = {}) => {
    const code = mappedAccountCode(value, chartAccounts);
    if (String(country || '').toUpperCase() !== 'NO') return { allowed: false, code, reasonCode: 'country_not_supported' };
    if (!/^\d{4,}$/.test(code)) return { allowed: false, code, reasonCode: 'account_missing' };
    if (/^[12]/.test(code)) return { allowed: false, code, reasonCode: 'balance_account' };
    if (/^5/.test(code)) return { allowed: false, code, reasonCode: 'payroll_personnel' };
    if (/^60/.test(code)) return { allowed: false, code, reasonCode: 'depreciation' };
    if (/^8/.test(code)) return { allowed: false, code, reasonCode: 'finance_tax' };
    if (noVatAccountCodes.has(code.slice(0, 4))) return { allowed: false, code, reasonCode: 'known_no_vat_expense' };
    return { allowed: true, code, reasonCode: 'transaction_may_carry_vat' };
  };

  const sameAccount = (fromAccount, toAccount) => {
    const fromCode = accountIdentity(fromAccount);
    const toCode = accountIdentity(toAccount);
    return Boolean(fromCode && toCode && fromCode === toCode);
  };

  // Mirror the backend term-lock exception; a zero rate alone does not prove no VAT reporting.
  const isWithoutVat = (posting, country) => {
    if (String(country || '').toUpperCase() !== 'NO') return false;
    const lines = Array.isArray(posting?.manualPostingLines) ? posting.manualPostingLines : [];
    const noCode = (value) => ['', 'none', 'ingen mva'].includes(String(value || '').trim().toLowerCase());
    return lines.length > 0 && lines.every((line) => line && ['from', 'to'].every((side) =>
      noCode(line[`${side}VatCode`]) && noCode(line[`${side}VatKind`]) &&
      !String(line[`${side}ReportVatCode`] || '').trim() && Number(line[`${side}VatRate`] || 0) === 0,
    ));
  };
  const vatSignature = (posting) => {
    const num = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;
    return JSON.stringify({ date: posting?.date || '', amountNet: num(posting?.amountNet),
      lines: (Array.isArray(posting?.manualPostingLines) ? posting.manualPostingLines : []).map((line) => ({
        fromAmount: num(line.fromAmount), fromVatCode: line.fromVatCode || '',
        fromVatRate: num(line.fromVatRate), fromVatKind: line.fromVatKind || '',
        fromReportVatCode: line.fromReportVatCode || '',
        toAmount: num(line.toAmount), toVatCode: line.toVatCode || '',
        toVatRate: num(line.toVatRate), toVatKind: line.toVatKind || '',
        toReportVatCode: line.toReportVatCode || '',
      })),
    });
  };
  const voucherWithoutVat = (state, voucher) => {
    const source = (Array.isArray(state?.expenses) ? state.expenses : []).find((entry) => entry.id === (voucher?.sourceId || voucher?.source?.id)) || voucher?.source;
    return Boolean(source?.manualPosting && isWithoutVat(source, state?.settings?.country));
  };
  const bankPayments = (posting) => {
    if (!posting || ['draft', 'annulled'].includes(posting.status)) return [];
    const lines = Array.isArray(posting.manualPostingLines) ? posting.manualPostingLines : [];
    const codes = [...new Set(lines.flatMap((line) => [line.fromAccount, line.toAccount]).map((value) => String(value || '').trim()).filter((code) => /^19[2-9]\d$/.test(code)))];
    if (!codes.length && posting.bankTrace) codes.push(posting.bankTrace.bankAccountCode || posting.bankTrace.statementBankAccountCode || '1920');
    return codes.map((code) => ({ date: posting.date, amount: Number(posting.amountNet) || 0, settlementAccountCode: code, paymentSource: 'company', status: posting.status || 'active' }));
  };
  const copyDraft = (source, id) => ({
    id, status: 'draft', manualPosting: true, source: 'manual_posting', date: '',
    description: source.description || '', copiedFromPostingId: source.id,
    copiedFromVoucherNumber: source.voucherNumber || source.id,
    manualPostingLines: (source.manualPostingLines || []).map((line, index) => ({
      id: `${id}-line-${index + 1}`,
      ...Object.fromEntries(Object.entries(line).filter(([key]) => /^(from|to)(Account|AccountLabel|Amount|VatCode|Counterparty(Type|Id|Name|Number))$/.test(key))),
    })),
  });
  scope.PlummeManualPostingRules = { accountCode, mappedAccountCode, vatEligibility, sameAccount, isWithoutVat, vatSignature, voucherWithoutVat, bankPayments, copyDraft };
})(typeof window !== 'undefined' ? window : globalThis);
